import { useState } from 'react'
import SearchIcon from '@material-ui/icons/Search'

type Option = {
  value: string
  label: string
}

type SelectSearchProps = {
  key: string
  id: string
  placeholder: string
  options: Option[]
  selectClassName?: string
  onSelect: (selection: string[]) => void
  onToggle?: () => void
  isOpen?: boolean
  withSearch?: boolean
  multiple?: boolean
  // caps how many options are displayed in the dropdown
  cap?: number
  // limits how many options can be selected max
  limit?: number
  isLoading?: boolean
  value?: string[]
}

export const SelectSearch = ({
  key,
  id,
  placeholder,
  options,
  selectClassName = '',
  onToggle,
  onSelect,
  isOpen,
  withSearch = false,
  multiple = false,
  cap,
  limit,
  isLoading,
  value,
}: SelectSearchProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [internalSelected, setInternalSelected] = useState<string[]>([])
  const selected = value ?? internalSelected
  const [search, setSearch] = useState('')

  const toggleDropdown = () => {
    if (onToggle) {
      onToggle()
    } else {
      setIsDropdownOpen(!isDropdownOpen)
    }
  }

  const toggleOption = (val: string) => {
    const isSelected = selected.includes(val)

    if (multiple) {
      if (!isSelected && limit && selected.length >= limit) {
        return
      }

      const newSelected = isSelected
        ? selected.filter((v) => v !== val)
        : [...selected, val]

      if (!value) setInternalSelected(newSelected)
      onSelect(newSelected)
    } else {
      const newSelected = [val]
      setIsDropdownOpen(false)

      if (!value) setInternalSelected(newSelected)
      onSelect(newSelected)
    }
  }

  const clearSelection = () => {
    if (!value) setInternalSelected([])
    onSelect([])
    setSearch('')
  }

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase()),
  )

  const displayOptions =
    withSearch && cap
      ? filteredOptions.slice(0, cap)
      : withSearch
      ? filteredOptions
      : options

  return (
    <div key={key} className='relative sm:w-64 w-full'>
      <button
        id={id}
        onClick={toggleDropdown}
        type='button'
        className={`w-full h-[40px] border-[0.8px] border-[#F4F4F4] shadow-[0px_0.4px_2px_0px_rgba(0,0,0,0.25)] bg-[#FFF] hover:bg-[#EBFBFF] ${
          selected.length > 0 ? 'text-[#37A2BA]' : 'text-[#00323D]'
        } font-medium focus:outline-none rounded-[8px] text-base p-[12px_16px] inline-flex items-center justify-between ${selectClassName}`}
      >
        <span className='truncate max-w-[calc(100%-20px)] block'>
          {selected.length === 0
            ? placeholder
            : multiple
            ? `${selected.length} selected`
            : options.find((opt) => opt.value === selected[0])?.label ||
              placeholder}
        </span>
        {isDropdownOpen || isOpen ? (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='12'
            height='12'
            viewBox='0 0 12 12'
            fill='none'
          >
            <path d='M6 3L12 9L0 9L6 3Z' fill='#37A2BA' />
          </svg>
        ) : (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='12'
            height='12'
            viewBox='0 0 12 12'
            fill='none'
          >
            <path d='M6 9L0 3H12L6 9Z' fill='#37A2BA' />
          </svg>
        )}
      </button>

      {(isDropdownOpen || isOpen) && (
        <div className='absolute mt-1 w-full p-2 left-0 bg-white rounded-lg shadow-[0px_0.4px_4px_0px_rgba(34,81,143,0.25)] outline outline-[0.4px] outline-offset-[-0.4px] outline-zinc-100 flex flex-col gap-2 z-10'>
          {isLoading ? (
            <div className='text-sm text-zinc-500 p-2'>Loading...</div>
          ) : (
            <>
              {withSearch && (
                <div className='relative w-full'>
                  <input
                    type='text'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder='Search'
                    className="w-full h-11 pl-4 pr-10 bg-white rounded-lg border border-[#F4F4F4] shadow-[inset_0px_0.8px_2px_0px_rgba(0,0,0,0.15)] text-zinc-500 text-sm font-medium font-['DM_Sans'] placeholder:text-zinc-500 focus:outline-none"
                  />
                  <div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
                    <SearchIcon style={{ fontSize: 20, color: '#9CA3AF' }} />
                  </div>
                </div>
              )}

              <ul className='self-stretch flex flex-col gap-1 max-h-60 overflow-auto'>
                {displayOptions.length > 0 && selected.length > 0 && (
                  <li
                    onClick={clearSelection}
                    className='cursor-pointer p-1.5 rounded-md flex justify-start items-center gap-2.5 hover:bg-zinc-100'
                  >
                    <div className="text-center text-gray-900 text-base font-medium font-['DM_Sans'] leading-normal tracking-wide">
                      Clear filter
                    </div>
                  </li>
                )}

                {displayOptions.length > 0 ? (
                  displayOptions.map((option) => (
                    <li
                      key={option.value}
                      onClick={() => toggleOption(option.value)}
                      className='cursor-pointer p-1.5 rounded-md flex justify-start items-start sm:items-center gap-2.5 hover:bg-zinc-100'
                    >
                      <div className='w-4 h-4 relative'>
                        <div
                          className={`w-4 h-4 left-0 top-0 absolute rounded-sm shadow-[inset_0.1px_0.5px_4px_0px_rgba(0,0,0,0.20)] ${
                            selected.includes(option.value)
                              ? 'bg-[#B8E0DB]'
                              : 'bg-slate-300'
                          }`}
                        />
                      </div>
                      <div
                        title={option.label}
                        className="text-gray-900 text-base font-medium font-['DM_Sans'] leading-normal tracking-wide whitespace-normal break-words sm:truncate sm:whitespace-nowrap appearance-none max-w-full"
                      >
                        {option.label}
                      </div>
                    </li>
                  ))
                ) : (
                  <li className='p-1.5 text-gray-400 text-sm'>No options</li>
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}
